import React, { createContext, useState, useEffect, useContext } from 'react';
import AuthService from '../services/authService';
import { unregisterWebDeviceToken } from '../services/fcmClient';
import { isTokenExpired, millisUntilExpiry } from '../utils/token';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      try {
        // First check localStorage without making API calls
        const token = localStorage.getItem('token');
        const currentUser = AuthService.getCurrentUser();

        // Treat a missing OR expired token as logged-out up front, so route
        // guards send the user to /login instead of letting them browse with a
        // dead session (the previous behaviour: pages opened but data was empty).
        if (currentUser && token && !isTokenExpired(token)) {
          // Ensure user has an id property
          if (!currentUser.id && currentUser.userId) {
            currentUser.id = currentUser.userId;
          }

          // Set user from localStorage immediately to avoid blocking UI
          setUser(currentUser);

          // Optional: Validate token in background later
          // This will happen after the UI is already loaded
          setTimeout(() => {
            validateUserInBackground(currentUser);
          }, 2000); // Delay validation to prioritize UI rendering
        } else if (token || currentUser) {
          // Stale/expired session — clear it locally (no server call needed).
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        // Always set loading to false to render UI
        setLoading(false);
      }
    };
    
    // Background validation function - doesn't block UI rendering
    const validateUserInBackground = async (currentUser) => {
      try {
        // Only validate if we have a token
        if (localStorage.getItem('token')) {
          const userInfo = await AuthService.getUserInfo();
          
          if (userInfo && userInfo.user) {
            const serverUser = userInfo.user;
            
            // Ensure user has an id property
            if (!serverUser.id && serverUser.userId) {
              serverUser.id = serverUser.userId;
            }
            
            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(serverUser));
            
            // Validate role if needed
            if (currentUser.role !== serverUser.role) {
              console.log(`Role mismatch: localStorage (${currentUser.role}) vs server (${serverUser.role})`);
              AuthService.logout();
              setUser(null);
              setError(`Your role has changed. Please login with your current role: ${serverUser.role}`);
              return;
            }
            
            // Update user state with fresh data
            setUser(serverUser);
          }
        }
      } catch (error) {
        // API error - don't log out automatically, just log the error
        // This prevents blocking access when backend is unavailable
        console.warn('Background validation error:', error);
        
        // Only log out if we're sure it's an auth error (401)
        if (error.response && error.response.status === 401) {
          console.error('Token invalid or expired');
          AuthService.logout();
          setUser(null);
        }
      }
    };

    initAuth();
  }, []);

  // React to a session ending from anywhere (axios 401 handler dispatches
  // 'auth:logout'), and proactively log out the moment the token expires while
  // the user sits idle on a page — both clear `user` so the route guard
  // redirects to /login within the SPA, no manual relogin needed.
  useEffect(() => {
    const handleForcedLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    };
    window.addEventListener('auth:logout', handleForcedLogout);

    let expiryTimer;
    const token = localStorage.getItem('token');
    if (user && token) {
      if (isTokenExpired(token)) {
        handleForcedLogout();
      } else {
        // Fire slightly before the real expiry so the UI redirects cleanly.
        const delay = Math.max(0, millisUntilExpiry(token) - 1000);
        // setTimeout caps at ~24.8 days; sessions are far shorter, so this is safe.
        expiryTimer = setTimeout(handleForcedLogout, delay);
      }
    }

    return () => {
      window.removeEventListener('auth:logout', handleForcedLogout);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [user]);

  const login = async (credentials) => {
    setError(null);
    try {
      const data = await AuthService.login(credentials);
      if (data && data.user && data.token) {
        setUser(data.user);
      }
      return data;
    } catch (error) {
      setError(error.message || 'Login failed');
      throw error;
    }
  };

  // Google OAuth functionality has been removed

  const logout = () => {
    AuthService.logout();
    unregisterWebDeviceToken();
    setUser(null);
  };

  // Tambahkan fungsi untuk memperbarui data user
  const updateUserContext = (updatedUserData) => {
    setUser(updatedUserData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    updateUserContext, // Tambahkan ke value yang diekspor
    token: localStorage.getItem('token') // Export token untuk compatibility
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
